/**
 * Button Wear System
 * Tracks button durability and handles identity migration when exhausted
 */

import { ButtonColor } from './SequenceManager';

export interface ButtonWear {
  color: ButtonColor;
  maxUses: number;
  remainingUses: number;
  wearLevel: number; // 0.0 to 1.0, where 1.0 is fully worn
}

export interface WearConfig {
  enabled: boolean;
  minUses: number; // Minimum uses before exhaustion
  maxUses: number; // Maximum uses before exhaustion
  visualThreshold: number; // Wear level at which visual degradation starts (0.0-1.0)
}

const DEFAULT_CONFIG: WearConfig = {
  enabled: false,
  minUses: 15,
  maxUses: 30,
  visualThreshold: 0.4
};

export class ButtonWearSystem {
  private wear: Map<ButtonColor, ButtonWear> = new Map();
  private config: WearConfig;

  constructor(config: Partial<WearConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Initialize wear tracking for all buttons
   */
  initialize(colors: ButtonColor[]): void {
    this.wear.clear();
    
    colors.forEach(color => {
      const maxUses = Math.floor(
        this.config.minUses + Math.random() * (this.config.maxUses - this.config.minUses)
      );
      
      this.wear.set(color, {
        color,
        maxUses,
        remainingUses: maxUses,
        wearLevel: 0
      });
    });
  }

  /**
   * Record a button use and update wear
   * Returns true if button is still usable, false if exhausted
   */
  useButton(color: ButtonColor): boolean {
    const buttonWear = this.wear.get(color);
    if (!buttonWear) return true; // Not tracked, allow use

    buttonWear.remainingUses = Math.max(0, buttonWear.remainingUses - 1);
    buttonWear.wearLevel = 1 - (buttonWear.remainingUses / buttonWear.maxUses);

    return buttonWear.remainingUses > 0;
  }

  /**
   * Check if a button is exhausted
   */
  isExhausted(color: ButtonColor): boolean {
    const buttonWear = this.wear.get(color);
    return buttonWear ? buttonWear.remainingUses <= 0 : false;
  }

  /**
   * Get wear info for a button
   */
  getWear(color: ButtonColor): ButtonWear | undefined {
    return this.wear.get(color);
  }

  /**
   * Get all wear states
   */
  getAllWear(): Map<ButtonColor, ButtonWear> {
    return new Map(this.wear);
  }

  /**
   * Migrate identity from exhausted button to target
   * Swaps color assignments while preserving positions
   */
  migrateIdentity(exhaustedColor: ButtonColor, targetColor: ButtonColor): void {
    const exhaustedWear = this.wear.get(exhaustedColor);
    const targetWear = this.wear.get(targetColor);

    if (!exhaustedWear || !targetWear) return;

    // Swap the color identities in the wear map
    this.wear.set(exhaustedColor, {
      ...targetWear,
      color: exhaustedColor // Keep tracking at this position
    });

    this.wear.set(targetColor, {
      ...exhaustedWear,
      color: targetColor // Keep tracking at this position
    });
  }

  /**
   * Find a non-exhausted button to receive migrated identity
   */
  findMigrationTarget(exhaustedColor: ButtonColor): ButtonColor | null {
    const colors: ButtonColor[] = ['red', 'green', 'blue', 'yellow'];
    const candidates = colors.filter(c => 
      c !== exhaustedColor && !this.isExhausted(c)
    );

    if (candidates.length === 0) return null;

    // Pick the least worn candidate
    let bestCandidate = candidates[0];
    let bestWearLevel = this.wear.get(bestCandidate)?.wearLevel || 1;

    candidates.forEach(candidate => {
      const wearLevel = this.wear.get(candidate)?.wearLevel || 1;
      if (wearLevel < bestWearLevel) {
        bestCandidate = candidate;
        bestWearLevel = wearLevel;
      }
    });

    return bestCandidate;
  }

  /**
   * Check if visual wear should be displayed
   */
  shouldShowWear(color: ButtonColor): boolean {
    const buttonWear = this.wear.get(color);
    if (!buttonWear) return false;
    return buttonWear.wearLevel >= this.config.visualThreshold;
  }

  /**
   * Reset all wear states (for new game)
   */
  reset(): void {
    const colors = Array.from(this.wear.keys());
    this.initialize(colors);
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<WearConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): WearConfig {
    return { ...this.config };
  }
}
